import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Save, Users, Settings2 } from "lucide-react";

interface Tier {
  name: string;
  min_referrals: number;
  monthly_kickback: number;
  badge: string;
  perks: string[];
}

export default function AdminReferralProgram() {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const queryClient = useQueryClient();
  const [editingTiers, setEditingTiers] = useState(false);

  // Fetch config
  const { data: config, isLoading } = useQuery({
    queryKey: ["admin-referral-config", organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("referral_config")
        .select("*")
        .eq("organization_id", organization!.id)
        .maybeSingle();
      return data;
    },
    enabled: !!organization?.id,
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ["admin-referral-stats", organization?.id],
    queryFn: async () => {
      const { data: referrals } = await supabase
        .from("referrals")
        .select("id, status, referrer_user_id")
        .eq("organization_id", organization!.id);

      const { data: rewards } = await supabase
        .from("referral_rewards")
        .select("amount")
        .eq("organization_id", organization!.id);

      const total = referrals?.length ?? 0;
      const active = referrals?.filter((r) => r.status === "active").length ?? 0;
      const uniqueReferrers = new Set(referrals?.map((r) => r.referrer_user_id)).size;
      const totalPaid = (rewards ?? []).reduce((sum, r) => sum + Number(r.amount), 0);

      return { total, active, uniqueReferrers, totalPaid };
    },
    enabled: !!organization?.id,
  });

  // Fetch all referrals with details
  const { data: allReferrals = [] } = useQuery({
    queryKey: ["admin-referrals-list", organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("referrals")
        .select(`
          id, status, created_at, activated_at, paused_at,
          welcome_bonus_granted, referrer_first_bonus_granted,
          referrer:profiles!referrals_referrer_user_id_fkey(full_name),
          referred:profiles!referrals_referred_user_id_fkey(full_name)
        `)
        .eq("organization_id", organization!.id)
        .order("created_at", { ascending: false });
      return data ?? [];
    },
    enabled: !!organization?.id,
  });

  // Config form state
  const [form, setForm] = useState<{
    enabled: boolean;
    welcome_bonus_amount: number;
    first_referral_bonus: number;
    tiers: Tier[];
  }>({
    enabled: false,
    welcome_bonus_amount: 100,
    first_referral_bonus: 200,
    tiers: [],
  });

  // Initialize form when config loads
  const _configLoaded = !!config;
  useState(() => {
    if (config) {
      setForm({
        enabled: config.enabled,
        welcome_bonus_amount: Number(config.welcome_bonus_amount),
        first_referral_bonus: Number(config.first_referral_bonus),
        tiers: config.tiers as Tier[],
      });
    }
  });

  // Update form when config changes
  if (config && form.tiers.length === 0 && (config.tiers as Tier[]).length > 0) {
    setForm({
      enabled: config.enabled,
      welcome_bonus_amount: Number(config.welcome_bonus_amount),
      first_referral_bonus: Number(config.first_referral_bonus),
      tiers: config.tiers as Tier[],
    });
  }

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (config) {
        const { error } = await supabase
          .from("referral_config")
          .update({
            enabled: form.enabled,
            welcome_bonus_amount: form.welcome_bonus_amount,
            first_referral_bonus: form.first_referral_bonus,
            tiers: form.tiers as any,
            updated_at: new Date().toISOString(),
          })
          .eq("id", config.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("referral_config")
          .insert({
            organization_id: organization!.id,
            enabled: form.enabled,
            welcome_bonus_amount: form.welcome_bonus_amount,
            first_referral_bonus: form.first_referral_bonus,
            tiers: form.tiers as any,
          });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-referral-config"] });
      toast.success(t("admin:toast.referralSettingsSaved"));
    },
    onError: (err: Error) => toast.error(`${t("referralProgram.error")}: ${err.message}`),
  });

  // Status change mutation
  const statusMutation = useMutation({
    mutationFn: async ({ referralId, status }: { referralId: string; status: string }) => {
      const { error } = await supabase
        .from("referrals")
        .update({
          status,
          ...(status === "paused" ? { paused_at: new Date().toISOString() } : {}),
          ...(status === "active" ? { activated_at: new Date().toISOString(), paused_at: null } : {}),
        })
        .eq("id", referralId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-referrals-list"] });
      queryClient.invalidateQueries({ queryKey: ["admin-referral-stats"] });
      toast.success(t("admin:toast.referralStatusUpdated"));
    },
  });

  if (isLoading) {
    return <div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats overview */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-primary">{stats?.total ?? 0}</p>
            <p className="text-xs text-muted-foreground">{t("referralProgram.totalReferred")}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats?.active ?? 0}</p>
            <p className="text-xs text-muted-foreground">{t("referralProgram.active")}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{stats?.uniqueReferrers ?? 0}</p>
            <p className="text-xs text-muted-foreground">{t("referralProgram.ambassadors")}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{stats?.totalPaid ?? 0} kr</p>
            <p className="text-xs text-muted-foreground">{t("referralProgram.paidCredit")}</p>
          </CardContent>
        </Card>
      </div>

      {/* Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" /> {t("referralProgram.settings")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>{t("referralProgram.enableProgram")}</Label>
            <Switch
              checked={form.enabled}
              onCheckedChange={(v) => setForm({ ...form, enabled: v })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>{t("referralProgram.welcomeBonus")}</Label>
              <NumberInput
                value={form.welcome_bonus_amount}
                onValueChange={(v) => setForm({ ...form, welcome_bonus_amount: v })}
              />
              <p className="text-xs text-muted-foreground">{t("referralProgram.welcomeBonusHint")}</p>
            </div>
            <div className="space-y-1">
              <Label>{t("referralProgram.firstReferralBonus")}</Label>
              <NumberInput
                value={form.first_referral_bonus}
                onValueChange={(v) => setForm({ ...form, first_referral_bonus: v })}
              />
              <p className="text-xs text-muted-foreground">{t("referralProgram.firstReferralBonusHint")}</p>
            </div>
          </div>

          {/* Tiers */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="font-semibold">{t("referralProgram.tiers")}</Label>
              <Button variant="ghost" size="sm" onClick={() => setEditingTiers(!editingTiers)}>
                {editingTiers ? t("referralProgram.done") : t("referralProgram.edit")}
              </Button>
            </div>

            {form.tiers.map((tier, i) => (
              <div key={i} className="flex items-center gap-3 p-2 rounded border">
                <span className="text-xl">{tier.badge}</span>
                {editingTiers ? (
                  <>
                    <Input
                      className="w-24"
                      value={tier.name}
                      onChange={(e) => {
                        const tiers = [...form.tiers];
                        tiers[i] = { ...tiers[i], name: e.target.value };
                        setForm({ ...form, tiers });
                      }}
                    />
                    <Input
                      className="w-16"                      value={tier.min_referrals}
                      onChange={(e) => {
                        const tiers = [...form.tiers];
                        tiers[i] = { ...tiers[i], min_referrals: Number(e.target.value) };
                        setForm({ ...form, tiers });
                      }}
                    />
                    <span className="text-xs">min</span>
                    <Input
                      className="w-20"                      value={tier.monthly_kickback}
                      onChange={(e) => {
                        const tiers = [...form.tiers];
                        tiers[i] = { ...tiers[i], monthly_kickback: Number(e.target.value) };
                        setForm({ ...form, tiers });
                      }}
                    />
                    <span className="text-xs">kr/mån</span>
                  </>
                ) : (
                  <>
                    <span className="font-medium">{tier.name}</span>
                    <span className="text-sm text-muted-foreground">{tier.min_referrals}+ {t("referralProgram.referrals")}</span>
                    <span className="text-sm font-medium ml-auto">{tier.monthly_kickback} {t("referralProgram.krPerMonth")}</span>
                  </>
                )}
              </div>
            ))}
          </div>

          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} className="w-full gap-2">
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {t("referralProgram.saveSettings")}
          </Button>
        </CardContent>
      </Card>

      {/* Referral list */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" /> {t("referralProgram.allReferrals")} ({allReferrals.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {allReferrals.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">{t("referralProgram.noReferralsYet")}</p>
          ) : (
            <div className="space-y-2">
              {allReferrals.map((ref: any) => (
                <div key={ref.id} className="flex items-center justify-between p-3 rounded-lg border text-sm">
                  <div className="flex-1">
                    <p>
                      <strong>{(ref.referrer as any)?.full_name || "?"}</strong>
                      {" → "}
                      {(ref.referred as any)?.full_name || "?"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(ref.created_at).toLocaleDateString("sv")}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={ref.status === "active" ? "default" : ref.status === "paused" ? "secondary" : "outline"}
                    >
                      {ref.status === "active" ? t("referralProgram.statusActive") : ref.status === "paused" ? t("referralProgram.statusPaused") : ref.status === "pending" ? t("referralProgram.statusPending") : ref.status}
                    </Badge>
                    <Select
                      value={ref.status}
                      onValueChange={(v) => statusMutation.mutate({ referralId: ref.id, status: v })}
                    >
                      <SelectTrigger className="w-24 h-7 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">{t("referralProgram.statusPending")}</SelectItem>
                        <SelectItem value="active">{t("referralProgram.statusActive")}</SelectItem>
                        <SelectItem value="paused">{t("referralProgram.statusPaused")}</SelectItem>
                        <SelectItem value="expired">{t("referralProgram.statusExpired")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
